import React, { useState, createContext, useContext, useRef } from 'react';
import { Animated, StyleProp, ViewStyle } from 'react-native';
import { useTheme, BoxProps } from '@/lib/styling';
import { Theme } from '@/theme';
import { Box, Text, PressableBox } from '@/components/ui/primitives';
import { getScreenSize } from '../../lib/responsive';

interface TabsContextType {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
};

interface TabsProps extends BoxProps {
  defaultValue: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  className?: string; // Compatibility
}

interface TabsListProps extends BoxProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  className?: string; // Compatibility
}

interface TabsTriggerProps extends React.ComponentProps<typeof PressableBox> {
  value: string;
  children: React.ReactNode;
  className?: string; // Compatibility
}

interface TabsContentProps extends BoxProps {
  value: string;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  className?: string; // Compatibility
}

export function Tabs({ defaultValue, value, onValueChange, children, style, ...props }: TabsProps) {
  const [uncontrolledTab, setUncontrolledTab] = useState(defaultValue);
  const activeTab = value ?? uncontrolledTab;
  const setActiveTab = (nextValue: string) => {
    if (value === undefined) {
      setUncontrolledTab(nextValue);
    }
    onValueChange?.(nextValue);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <Box style={style as any} {...props}>
        {children}
      </Box>
    </TabsContext.Provider>
  );
}

export function TabsList({ children, style, ...props }: TabsListProps) {
  return (
    <Box marginBottom="m" position="relative">
      <Box
        borderRadius="none"
        padding="none"
        backgroundColor="transparent"
        borderWidth={0}
        borderBottomWidth={1}
        borderColor="border"
        shadowColor="transparent"
        shadowOpacity={0}
        shadowOffset={{ width: 0, height: 0 }}
        shadowRadius={0}
        elevation={0}
        style={style}
        {...props}
      >
        <Box flexDirection="row" flexWrap="wrap" justifyContent="flex-start" width="100%" gap="s">
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return child;
            return child;
          })}
        </Box>
      </Box>
    </Box>
  );
}

export function TabsTrigger({ value, children, style, ...props }: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabsContext();
  const theme = useTheme<Theme>();
  const isActive = activeTab === value;

  // Animated values for smooth transitions
  const animatedScale = useRef(new Animated.Value(1)).current;
  const screenSize = getScreenSize();
  const isSmallScreen = screenSize === 'small';
  const isLargeScreen = screenSize === 'large' || screenSize === 'xlarge';
  const triggerStyle = { minWidth: isLargeScreen ? '30%' : '47%' };
  const animatedStyle = {
    width: '100%',
    transform: [{ scale: animatedScale }],
  } as any;

  const handlePressIn = () => {
    if (isActive) {
      Animated.spring(animatedScale, {
        toValue: isSmallScreen ? 0.99 : 0.98,
        useNativeDriver: false,
        tension: 200,
        friction: 12,
      }).start();
    }
  };

  const handlePressOut = () => {
    if (isActive) {
      Animated.spring(animatedScale, {
        toValue: 1,
        useNativeDriver: false,
        tension: 200,
        friction: 12,
      }).start();
    }
  };

  const handlePress = () => {
    setActiveTab(value);
  };

  const enhanceTabChildren = (node: React.ReactNode): React.ReactNode => {
    if (typeof node === 'string' || typeof node === 'number') {
        if (!isActive) return node;
        return (
            <Text
                variant="button"
                color="primary"
            >
                {node}
            </Text>
        );
    }

    return React.Children.map(node, (child) => {
      if (!React.isValidElement(child)) return child;
      
      const element = child as React.ReactElement<any>;

      if (element.type === React.Fragment) {
        return enhanceTabChildren(element.props.children);
      }

      if (element.props.children && typeof element.props.children !== 'string' && typeof element.props.children !== 'number') {
         return React.cloneElement(element, {
           children: enhanceTabChildren(element.props.children)
         });
      }

      if (isActive) {
          const newProps: any = {};

          if (element.type === Text) {
             newProps.color = 'primary';
          } else {
             newProps.color = theme.colors.primary;
          }
          
          const styleOverride = { 
              color: theme.colors.primary,
              fontFamily: theme.typography.fontFamily.mono,
              fontWeight: '700',
              textTransform: 'uppercase',
          };

          if (element.props.style) {
              if (Array.isArray(element.props.style)) {
                  newProps.style = [...element.props.style, styleOverride];
              } else {
                  newProps.style = [element.props.style, styleOverride];
              }
          } else {
              newProps.style = styleOverride;
          }

          return React.cloneElement(element, newProps);
      }

      return element;
    });
  };

  return (
    <PressableBox
      onPress={handlePress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      width={isLargeScreen ? '30%' : '47%'}
      flexGrow={0}
      flexShrink={0}
      style={[triggerStyle, style as any]}
      {...props}
    >
      <Box
        width="100%"
        alignItems="center"
        justifyContent="center"
        borderRadius="none"
        overflow="hidden"
        borderWidth={0}
        borderBottomWidth={isActive ? 2 : 0}
        borderColor="primary"
        backgroundColor="transparent"
      >
        <Animated.View style={animatedStyle}>
        <Box
          width="100%"
          alignItems="center"
          justifyContent="center"
          paddingHorizontal={isSmallScreen ? 's' : 'm'}
          paddingVertical={isSmallScreen ? 'xs' : 's'}
        >
          {enhanceTabChildren(children)}
        </Box>
        </Animated.View>
      </Box>
    </PressableBox>
  );
}

export function TabsContent({ value, children, style, ...props }: TabsContentProps) {
  const { activeTab } = useTabsContext();

  if (activeTab !== value) {
    return null;
  }

  return (
    <Box style={style as any} {...props}>
      {children}
    </Box>
  );
}
